import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, Card, Button, CircularProgress, Snackbar, Alert,
  LinearProgress, Chip, Stack, Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { API_BASE_URL } from "../../config";
import {
  getIntakeSession,
  fetchIntakeProgress,
  fetchIntakeOutputs,
} from "../../services/intakeClient";
import { RISK_TIER_COLORS } from "../../services/archetypeClient";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";
import { computeOverallScore, computeSectionCompletionPct, isSectionRequiredForIntake, isStaleUnlinkedCapture, formatFactQuestionText, parseIntakeStartedAt, countCapturedBySource } from "../../utils/chatUtils";
import { resolveIntakeStartedAt } from "../../utils/intakeStartedStorage";
import { exportElementToPdf, summaryPdfFilename } from "../../utils/exportSummaryPdf";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import { INTAKE_PROFILE_ROW_PX } from "../../components/layout/layoutConstants";
import { ReactComponent as ProfileConfidenceIcon } from "../../asserts/logo/ProfileConfidence.svg";
import { ReactComponent as CompletionIcon } from "../../asserts/logo/Completion.svg";
import { ReactComponent as InProgressIcon } from "../../asserts/logo/InProgress.svg";
import { ReactComponent as NotStartedIcon } from "../../asserts/logo/NotStarted.svg";
import { useAuth } from "../../context/AuthContext";
import { canChatWrite } from "../../utils/entitlements";
import { postGate0FeaturesEnabled } from "../../config/productFeatures";

/**
 * Back-arrow placement — same 24px / 16px ink standard as App Identity and
 * the Gate 0 chatbot header. CSS backs out the 6px hit-target inset and
 * Lato's ~1px capital side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
const HEADER_TEXT_COLUMN_INSET_PX = BACK_ARROW_BUTTON_SIZE_PX + BACK_ARROW_GAP_RIGHT_PX;

const severityColor = (sev) => {
  const s = String(sev || "").toLowerCase();
  if (s.includes("high") || s.includes("critical")) return INTAKE_COLORS.red;
  if (s.includes("medium") || s.includes("moderate")) return INTAKE_COLORS.amber;
  if (s.includes("low")) return INTAKE_COLORS.green;
  return INTAKE_COLORS.g5;
};

const severityBg = (sev) => {
  const s = String(sev || "").toLowerCase();
  if (s.includes("high") || s.includes("critical")) return INTAKE_COLORS.redLight;
  if (s.includes("medium") || s.includes("moderate")) return INTAKE_COLORS.amberLight;
  if (s.includes("low")) return INTAKE_COLORS.greenLight;
  return INTAKE_COLORS.g1;
};

const trustLabels = Object.fromEntries(
  TRUST_ATTRIBUTES.map((attr) => [attr.toLowerCase(), trustAttributeLabel(attr)])
);

const factRowTextSx = {
  fontFamily: INTAKE_FONT,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#383838",
};

function SummaryFactRow({ item, capd }) {
  const rawValue = capd?.value ?? capd?.answer ?? "";
  const hasValue = rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== "";
  const displayValue = hasValue ? String(rawValue) : "?";
  const label = formatFactQuestionText(item.text || item.label || item.id);

  return (
    <Box
      sx={sectionRowGrid({
        py: 0.75,
        alignItems: "flex-start",
        minHeight: 24,
        px: 1.25,
        bgcolor: INTAKE_COLORS.surface,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={factDotCellSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: INTAKE_COLORS.primary, flexShrink: 0 }} />
      </Box>
      <Box
        sx={{
          gridColumn: "2 / -1",
          display: "block",
          alignItems: "center",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Box component="span" sx={{ ...factRowTextSx, fontWeight: 400, flexShrink: 0 }}>
          {label}
          {" : "}
        </Box>
        <Box
          component="span"
          sx={{
            ...factRowTextSx,
            fontWeight: 700,
            color: hasValue ? "#383838" : INTAKE_COLORS.g4,
            /*overflow: "hidden",
            textOverflow: "ellipsis",*/
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            minWidth: 0,
          }}
        >
          {displayValue}
        </Box>
      </Box>
    </Box>
  );
}

const SECTION_STATUS_STYLE = {
  complete: { badgeBg: "#E8FCF3", countColor: "#00754E", barColor: "#14B871" },
  inProgress: { badgeBg: "#FFF6E0", countColor: "#8A5C00", barColor: "#E4A11A" },
  notStarted: { badgeBg: "#F5F5F5", countColor: "#707070", barColor: "#BBBBBB" },
};

/** Same outer width for every count pill so 6/6 and 10/10 share a left edge. */
const STATUS_BADGE_WIDTH = 76;

const CHEVRON_WIDTH = 18;
const SECTION_HEADER_GAP = 0.75;

function sectionRowGrid(extra = {}) {
  return {
    display: "grid",
    gridTemplateColumns: `${CHEVRON_WIDTH}px 1fr 80px`,
    columnGap: "4px",
    boxSizing: "border-box",
    ...extra,
  };
}

const chevronCellSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  width: "100%",
  pt: 0.25,
};

const factDotCellSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  minHeight: 24,
};

const summaryInfoIconSx = {
  width: 16,
  height: 16,
  display: "block",
  cursor: "pointer",
  flexShrink: 0,
};

function SummaryInfoTooltip({ title, children }) {
  return (
    <Tooltip
      arrow
      placement="bottom-end"
      enterDelay={120}
      title={title}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "#000000",
            color: "#FFFFFF",
            borderRadius: "6px",
            p: "16px",
            maxWidth: 430,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.28)",
            "& .MuiTypography-root": {
              fontFamily: INTAKE_FONT,
            },
          },
        },
        arrow: {
          sx: {
            color: "#000000",
          },
        },
      }}
    >
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 1 }}>
        {children}
      </Box>
    </Tooltip>
  );
}

/** Ghost primary ? matches Expand All / gate Back buttons */
const ghostPrimaryBtnSx = {
  height: 32,
  minHeight: 32,
  borderRadius: "4px",
  border: "1px solid #005BF0",
  color: "#005BF0",
  bgcolor: "#FFFFFF",
  textTransform: "none",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: INTAKE_FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  pt: "4px",
  pb: "4px",
  px: "16px",
  boxShadow: "0px 1px 2px 0px #0000000A",
  "&:hover": {
    bgcolor: "#005BF014",
    border: "1px solid #005BF0",
    boxShadow: "0px 1px 2px 0px #0000000A",
  },
};

/** Footer primary — same Default / Hover / Focus / Active as chatbot View Summary. */
const FOOTER_PRIMARY_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const footerPrimaryBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 32,
  minHeight: 32,
  px: "16px",
  py: "4px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  bgcolor: "#005BF0",
  backgroundColor: "#005BF0",
  backgroundImage: "none",
  color: "#FFFFFF",
  border: "1px solid transparent",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#0046B8",
    backgroundColor: "#0046B8",
    backgroundImage: "none",
    color: "#FFFFFF",
    border: "1px solid transparent",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "2px solid #005BF0",
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#0046B8 !important",
    backgroundColor: "#0046B8 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "1px solid transparent",
    boxShadow: FOOTER_PRIMARY_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#005BF0",
    color: "#FFFFFF",
    opacity: 0.6,
  },
};

function fmtDateTime(value) {
  if (!value) return "?";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "?";
  return dt.toLocaleString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const RISK_MODIFIER_LABELS = {
  PII: "Personally identifiable information handling",
  ENT: "Enterprise-wide usage",
  DEC: "Decision support",
  PROD: "Production deployment",
  REG: "Regulated domain",
};

const INHERENT_RISK_BY_TYPE = {
  CHATBOT: "LOW", GEN: "LOW", RAG: "MEDIUM", STRUCTURED: "MEDIUM",
  CODE: "MEDIUM", DATA: "HIGH", VISION: "HIGH", AUDIO: "MEDIUM",
  AGENTIC: "CRITICAL", FINE: "HIGH", ENTERPRISE: "CRITICAL", MULTI: "CRITICAL",
};

/** Successful determination is an A1–A12 baseCode from saveArchetype, not an onboarding label. */
const DETERMINED_BASE_CODE = /^A(?:1[0-2]|[1-9])$/;

function isDeterminedBaseCode(code) {
  return DETERMINED_BASE_CODE.test(String(code || "").trim().toUpperCase());
}

function persistedDeterminedArchetype(gate0) {
  const detail = gate0?.archetype_detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  if (!isDeterminedBaseCode(detail.baseCode)) return null;
  const code = String(detail.baseCode).trim().toUpperCase();
  const stored = String(gate0?.archetype || "").trim().toUpperCase();
  const predictedArchetype = stored === code || stored.startsWith(`${code}-`)
    ? stored
    : [code, detail.baseType].filter(Boolean).join("-");
  return { ...detail, predictedArchetype };
}

function riskAssessmentRows(archetype) {
  if (!archetype) return [];
  const parts = String(archetype.predictedArchetype || "").toUpperCase().split("-");
  const baseType = String(archetype.baseType || parts[1] || "").toUpperCase();
  const inherent = INHERENT_RISK_BY_TYPE[baseType] || null;
  const rows = [];
  if (inherent && baseType) {
    rows.push({ key: "inherent", label: "Inherent Risk", text: `${baseType} archetype baseline risk is `, value: inherent });
  }
  ["PII", "ENT", "DEC", "PROD", "REG"].forEach((code) => {
    if (parts.includes(code)) rows.push({ key: code, label: code, text: RISK_MODIFIER_LABELS[code] });
  });
  if (archetype.riskTier) {
    const elevated = inherent && inherent !== String(archetype.riskTier).toUpperCase();
    rows.push({
      key: "combined",
      label: "Combined Risk",
      text: elevated ? "Elevated to " : "",
      value: String(archetype.riskTier).toUpperCase(),
    });
  }
  return rows;
}

const riskListSx = {
  fontFamily: "'Lato', sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "180%",
  letterSpacing: 0,
  color: "#383838",
};

const riskLabelSx = {
  fontFamily: "'Lato', sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "180%",
  letterSpacing: 0,
  color: "#383838",
};

const archetypeSectionHeadingSx = {
  fontFamily: "'Lato', sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 14,
  lineHeight: "18px",
  letterSpacing: 0,
  color: "#383838",
};

export default function SummaryPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWriteChat = canChatWrite(user?.roles);

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [outputs, setOutputs] = useState(null);
  const [toast, setToast] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});
  const [exporting, setExporting] = useState(false);
  const summaryExportRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        setLoading(true);

        await axios.get(`${API_BASE_URL}/projects/${projectId}`).catch(() => null);

        const sessionRes = await getIntakeSession(projectId);
        const sid = sessionRes?.data?.session_id;
        if (!sid) {
          setToast("No active intake session found for this project.");
          setToastSeverity("warning");
          setLoading(false);
          return;
        }
        const prog = await fetchIntakeProgress(projectId, sid);
        if (prog?.success && prog.data) {
          const startedAt = resolveIntakeStartedAt(projectId, {
            progressStartedAt: prog.data.started_at,
            sessionStartedAt: sessionRes?.data?.started_at,
          });
          setProgress({ ...prog.data, started_at: startedAt });
        }

        try {
          const out = await fetchIntakeOutputs(projectId, sid);
          if (out?.success && out.data) setOutputs(out.data);
          else if (out && (out.trust_profile || out.risks)) setOutputs(out);
        } catch {
          // Outputs may not exist yet ? that's fine.
        }

      } catch (err) {
        setToast(err.response?.data?.error || err.message || "Failed to load summary.");
        setToastSeverity("error");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const sections = progress?.framework_sections ?? [];

  // Determined only when archetype_detail.baseCode is A1–A12.
  // A non-empty gate0_data.archetype ("RAG", "Chatbot") does not count.
  const shownArchetype = persistedDeterminedArchetype(progress?.gate0_data);
  const archetypeReady = Boolean(shownArchetype);
  const assessmentRows = riskAssessmentRows(shownArchetype);

  const scores = progress?.scores ?? {};
  const captured = progress?.captured ?? {};
  const gate0Data = progress?.gate0_data || {};
  const overall = useMemo(
    () =>
      sections.length
        ? computeOverallScore(scores, sections)
        : progress?.overall_score || 0,
    [progress]
  );
  const started = parseIntakeStartedAt(progress?.started_at);

  const capturedBySection = useMemo(() => {
    const map = {};
    sections.forEach((s) => {
      map[s.id] = (s.items || [])
        .map((item) => {
          const cap = captured[item.id];
          if (!cap || isStaleUnlinkedCapture(item, cap, s.id)) return null;
          return { id: item.id, item, cap };
        })
        .filter(Boolean);
    });
    return map;
  }, [sections, captured]);

  const sourceCounts = useMemo(
    () => countCapturedBySource(sections, captured),
    [sections, captured]
  );
  const totalCount =
    sourceCounts.gate0
    + sourceCounts.doc
    + sourceCounts.chat
    + sourceCounts.inferred
    + sourceCounts.pending;
  const completedCount =
    sourceCounts.gate0 + sourceCounts.doc + sourceCounts.chat + sourceCounts.inferred;
  const pendingCount = sourceCounts.pending;
  const requiredSections = sections.filter((s) => isSectionRequiredForIntake(s)).length;
  const optionalSections = sections.length - requiredSections;

  const shownSections = useMemo(() => {
    if (sectionFilter === "required") return sections.filter((s) => isSectionRequiredForIntake(s));
    if (sectionFilter === "optional") return sections.filter((s) => !isSectionRequiredForIntake(s));
    return sections;
  }, [sectionFilter, sections]);

  useEffect(() => {
    if (!shownSections.length) return;
    setExpandedSections((prev) => {
      const next = { ...prev };
      let changed = false;
      shownSections.forEach((s) => {
        if (!(s.id in next)) {
          next[s.id] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [shownSections]);

  const fmt2 = (n) => String(Math.max(0, Number(n) || 0)).padStart(2, "0");
  const allShownExpanded = shownSections.length > 0 && shownSections.every((s) => Boolean(expandedSections[s.id]));
  const sectionStatus = (answered, total) => {
    if (!total || answered <= 0) return "notStarted";
    if (answered >= total) return "complete";
    return "inProgress";
  };
  const sectionStatusIcon = (status) => {
    if (status === "complete") return <CompletionIcon style={{ width: 18, height: 18, display: "block" }} />;
    if (status === "inProgress") return <InProgressIcon style={{ width: 14, height: 14, display: "block" }} />;
    return <NotStartedIcon style={{ width: 14, height: 14, display: "block" }} />;
  };
  const handleExport = async () => {
    if (!summaryExportRef.current || exporting) return;

    const previousExpanded = expandedSections;
    const expandedForExport = { ...expandedSections };
    shownSections.forEach((section) => {
      expandedForExport[section.id] = true;
    });

    setExporting(true);
    setExpandedSections(expandedForExport);

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      const filename = summaryPdfFilename(gate0Data.app_name);
      await exportElementToPdf(summaryExportRef.current, filename);
      setToast("Summary downloaded as PDF.");
      setToastSeverity("success");
    } catch (err) {
      console.error("Summary PDF export failed:", err);
      setToast("Failed to export summary. Please try again.");
      setToastSeverity("error");
    } finally {
      setExpandedSections(previousExpanded);
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100%",
        overflow: "hidden",
        bgcolor: INTAKE_COLORS.surface,
        fontFamily: INTAKE_FONT,
      }}
    >
      <IntakeGateTabsBar activeGate={0} showBack={false} showViewSummary={false} />
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "auto" }}>
        <Card
          elevation={0}
          sx={{
            border: "none",
            borderRadius: 0,
            bgcolor: INTAKE_COLORS.surface,
            mx: 0,
            width: "100%",
            boxShadow: "none",
          }}
        >
          <Box ref={summaryExportRef} sx={{ bgcolor: INTAKE_COLORS.surface }}>
          <Box sx={{ pl: `${HEADER_INSET_LEFT_PX}px`, pr: INTAKE_PROFILE_ROW_PX, py: 1, boxSizing: "border-box", bgcolor: INTAKE_COLORS.surface, borderBottom: `1px solid ${INTAKE_COLORS.grey20}` }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 0.2, pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`, boxSizing: "border-box", flexWrap: { xs: "wrap", md: "nowrap" } }}>
              <Typography
                sx={{
                  fontFamily: INTAKE_FONT,
                  width: { xs: "100%", lg: 824 },
                  minHeight: 19,
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: 16,
                  lineHeight: "100%",
                  letterSpacing: 0,
                  color: "#383838",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                New onboarding application
              </Typography>
              <Typography sx={{ fontFamily: INTAKE_FONT, fontSize: 12, fontWeight: 400, lineHeight: "20px", color: "#555555", textAlign: { xs: "left", md: "right" }, whiteSpace: { xs: "normal", md: "nowrap" } }}>
                {started ? (
                  <>
                    Started on{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#383838" }}>
                      {started.datePart}
                    </Box>
                    {" at "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#383838" }}>
                      {started.timePart}
                    </Box>
                  </>
                ) : (
                  "Started on ?"
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, mb: 0.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: `${HEADER_ROW_GAP_PX}px`,
                  minWidth: 0,
                  flex: "0 1 auto",
                  maxWidth: { xs: "100%", md: "calc(100% - 280px)" },
                }}
              >
                <IntakeHeaderBackButton
                  onClick={() => navigate(canWriteChat ? `/chat/${projectId}` : "/home")}
                />
                <Typography
                  component="span"
                  sx={{
                    ml: `${TITLE_NUDGE_LEFT_PX}px`,
                    flex: "0 1 auto",
                    minWidth: 0,
                    fontFamily: INTAKE_FONT,
                    fontWeight: 900,
                    fontStyle: "normal",
                    fontSize: { xs: 20, sm: 22, md: 24 },
                    lineHeight: { xs: "26px", sm: "28px", md: "30px" },
                    letterSpacing: 0,
                    color: "#383838",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {gate0Data.app_name || "Profile your AI"}
                </Typography>
                <Chip
                  label="Ready for Gate 1"
                  size="small"
                  sx={{
                    height: 24,
                    flexShrink: 0,
                    borderRadius: "12px",
                    px: "8px",
                    bgcolor: "#E8FCF3",
                    color: "#00754E",
                    fontFamily: INTAKE_FONT,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: "16px",
                    "& .MuiChip-label": {
                      px: 0,
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontFamily: INTAKE_FONT, fontSize: 11, color: INTAKE_COLORS.g4, textAlign: { xs: "left", md: "right" }, whiteSpace: { xs: "normal", md: "nowrap" }, flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
                Completed by {gate0Data.owner_name || "?"} on {fmtDateTime(progress?.updated_at || progress?.started_at)}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: INTAKE_FONT,
                width: "100%",
                minHeight: 19,
                fontSize: 16,
                fontWeight: 400,
                fontStyle: "normal",
                color: "#555555",
                lineHeight: { xs: "20px", md: "100%" },
                letterSpacing: 0,
                mb: 0.75,
                pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
                boxSizing: "border-box",
              }}
            >
              You&apos;ve met all requirements to proceed. Review your data, trust profile, and risk assessment below, then move to Gate 1.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "nowrap", pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`, boxSizing: "border-box" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                <Typography
                  sx={{
                    fontFamily: INTAKE_FONT,
                    minHeight: 24,
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: "24px",
                    letterSpacing: 0,
                    color: "#000000",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Profile confidence
                </Typography>
                <SummaryInfoTooltip
                  title={(
                    <Box>
                      <Typography sx={{ fontSize: 18, lineHeight: "24px", fontWeight: 700, mb: 1.25 }}>
                        What is profile confidence?
                      </Typography>
                      <Typography sx={{ fontSize: 14, lineHeight: "28px", fontWeight: 400 }}>
                        Overall completeness of your AI system profile based on all collected facts, risk coverage, and section threshold met.
                      </Typography>
                    </Box>
                  )}
                >
                  <ProfileConfidenceIcon style={summaryInfoIconSx} />
                </SummaryInfoTooltip>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, overall)}
                  sx={{
                    height: 6,
                    borderRadius: 6,
                    bgcolor: "#DDE8FB",
                    "& .MuiLinearProgress-bar": { bgcolor: "#005BF0", borderRadius: 6 },
                  }}
                />
              </Box>
              <Typography sx={{ fontFamily: INTAKE_FONT, fontSize: 11, color: "#005BF0", fontWeight: 700, whiteSpace: "nowrap" }}>
                {Math.round(overall)}%
              </Typography>
            </Box>
          </Box>

          {/* AI archetype — POC classification from the app summary */}
          <Box sx={{ px: INTAKE_PROFILE_ROW_PX, py: 2, bgcolor: "#F5F5F5", borderBottom: `1px solid ${INTAKE_COLORS.border}` }} data-export-hide>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                border: `1px solid ${INTAKE_COLORS.border}`,
                borderRadius: "12px",
                p: { xs: 2, md: 2.5 },
              }}
            >
              <Typography sx={{ fontFamily: INTAKE_FONT, fontWeight: 900, fontSize: 16, lineHeight: "22.5px", letterSpacing: "-0.38px", color: "#1F2335" }}>
                AI Archetype
              </Typography>

              {!archetypeReady ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.25 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "#000000", flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontFamily: INTAKE_FONT,
                      fontWeight: 400,
                      fontSize: 13,
                      lineHeight: "18px",
                      color: "#383838",
                    }}
                  >
                    Archetype is determined when you open this summary from chat.
                  </Typography>
                </Box>
              ) : null}

              {shownArchetype ? (
                <Box sx={{ mt: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    {shownArchetype.predictedArchetype ? (
                      <Typography
                        sx={{
                          fontFamily: "'Lato', sans-serif",
                          fontWeight: 700,
                          fontStyle: "normal",
                          fontSize: 13,
                          lineHeight: "18px",
                          letterSpacing: 0,
                          color: "#005BF0",
                        }}
                      >
                        {shownArchetype.predictedArchetype}
                      </Typography>
                    ) : null}
                    {shownArchetype.riskTier ? (
                      <Chip
                        label={shownArchetype.riskTier}
                        size="small"
                        sx={{
                          height: 26,
                          borderRadius: "16px",
                          fontFamily: INTAKE_FONT,
                          fontWeight: 700,
                          fontSize: 12,
                          bgcolor: (RISK_TIER_COLORS[shownArchetype.riskTier] || {}).bg || "#F1F5F9",
                          color: (RISK_TIER_COLORS[shownArchetype.riskTier] || {}).color || "#475569",
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ) : null}
                    {typeof shownArchetype.confidence === "number" ? (
                      <Typography
                        sx={{
                          fontFamily: "'Lato', sans-serif",
                          fontWeight: 700,
                          fontStyle: "normal",
                          fontSize: 12,
                          lineHeight: "16px",
                          letterSpacing: 0,
                          color: "#383838",
                        }}
                      >
                        {Math.round(shownArchetype.confidence * 100)}% confidence
                      </Typography>
                    ) : null}
                  </Box>

                  {shownArchetype.archetypeDescription ? (
                    <Typography
                      sx={{
                        fontFamily: "'Lato', sans-serif",
                        fontWeight: 400,
                        fontStyle: "normal",
                        fontSize: 14,
                        lineHeight: "21px",
                        letterSpacing: 0,
                        color: "#383838",
                        mt: 1.25,
                      }}
                    >
                      {shownArchetype.archetypeDescription}
                    </Typography>
                  ) : null}

                  {(assessmentRows.length > 0 || shownArchetype.riskDescription) ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={archetypeSectionHeadingSx}>
                        Risk assessment
                      </Typography>
                      {assessmentRows.length > 0 ? (
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                          {assessmentRows.map((row) => (
                            <Typography key={row.key} component="li" sx={riskListSx}>
                              <Box component="span" sx={riskLabelSx}>{row.label}</Box>
                              {" - "}
                              {row.text}
                              {row.value ? <Box component="span" sx={riskLabelSx}>{row.value}</Box> : null}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ ...riskListSx, mt: 0.5 }}>
                          {shownArchetype.riskDescription}
                        </Typography>
                      )}
                    </Box>
                  ) : null}

                  {(shownArchetype.recommendations?.length || 0) > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={archetypeSectionHeadingSx}>
                        Recommendations
                      </Typography>
                      <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                        {shownArchetype.recommendations.map((r, i) => (
                          <Typography
                            key={i}
                            component="li"
                            sx={{
                              fontFamily: "'Lato', sans-serif",
                              fontWeight: 400,
                              fontStyle: "normal",
                              fontSize: 14,
                              lineHeight: "20px",
                              letterSpacing: 0,
                              color: "#383838",
                            }}
                          >
                            {r}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ) : null}

                  {(shownArchetype.keyFocusAreas?.length || 0) > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: "'Lato', sans-serif",
                          fontWeight: 600,
                          fontStyle: "normal",
                          fontSize: 13,
                          lineHeight: "18px",
                          letterSpacing: 0,
                          color: "#000000",
                          mb: 1,
                        }}
                      >
                        Key focus areas
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {shownArchetype.keyFocusAreas.map((k, i) => (
                          <Chip
                            key={i}
                            label={k}
                            size="small"
                            sx={{
                              height: 28,
                              borderRadius: "16px",
                              bgcolor: INTAKE_COLORS.primary20,
                              color: "#005BF0",
                              "& .MuiChip-label": {
                                px: 1.25,
                                fontFamily: "'Lato', sans-serif",
                                fontWeight: 600,
                                fontStyle: "normal",
                                fontSize: 12,
                                lineHeight: "16px",
                                letterSpacing: 0,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Typography sx={{ fontFamily: INTAKE_FONT, fontSize: 14, color: "#555555", mt: 1 }}>
                  Not determined yet
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ px: INTAKE_PROFILE_ROW_PX, py: 1.5, borderBottom: `1px solid ${INTAKE_COLORS.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                <Typography
                  sx={{
                    width: { xs: "auto", md: 99 },
                    minHeight: 23,
                    fontFamily: INTAKE_FONT,
                    fontWeight: 900,
                    fontStyle: "normal",
                    fontSize: 16,
                    lineHeight: "22.5px",
                    letterSpacing: "-0.38px",
                    color: "#000000",
                  }}
                >
                  Section scores
                </Typography>
                <SummaryInfoTooltip
                  title={(
                    <Box>
                      <Typography sx={{ fontSize: 18, lineHeight: "24px", fontWeight: 700, mb: 1.25 }}>
                        How section scores are calculated:
                      </Typography>
                      <Typography sx={{ fontSize: 14, lineHeight: "28px", fontWeight: 400 }}>
                        Each section&apos;s score represents the percentage of facts collected and validated. Scores are weighted by source reliability:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, mt: 1.25, pl: 2.25 }}>
                        <Typography component="li" sx={{ fontSize: 14, lineHeight: "28px", fontWeight: 400, mb: 0.75 }}>
                          Gate 0 doc: 100% weight (verified documentation)
                        </Typography>
                        <Typography component="li" sx={{ fontSize: 14, lineHeight: "28px", fontWeight: 400 }}>
                          Chat intake: 90% weight (human-provided)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                >
                  <ProfileConfidenceIcon style={summaryInfoIconSx} />
                </SummaryInfoTooltip>
              </Box>
            </Box>
            <Typography
              sx={{
                mt: 0.5,
                width: { xs: "100%", md: 488 },
                minHeight: 18,
                fontFamily: INTAKE_FONT,
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: 14,
                lineHeight: "18px",
                letterSpacing: 0,
                color: "#555555",
              }}
            >
              Click rows to expand | Dashed border indicates optional sections
            </Typography>

            <Box sx={{ mt: 1.25, mx: { xs: -1.5, md: -2.5 }, border: `1px solid ${INTAKE_COLORS.border}`, bgcolor: "#FFFFFF", boxSizing: "border-box" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", borderBottom: `1px solid ${INTAKE_COLORS.border}`, minHeight: 85, bgcolor: "#F5F5F5", ml: "-1px", width: "calc(100% + 2px)" }}>
                {[
                  { label: "TOTAL", value: totalCount, color: "#383838" },
                  { label: "COMPLETED", value: completedCount, color: INTAKE_COLORS.greenDark },
                  { label: "PENDING", value: pendingCount, color: INTAKE_COLORS.g4 },
                ].map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      py: 1.25,
                      textAlign: "center",
                      borderRight: `1px solid ${INTAKE_COLORS.border}`,
                      "&:last-of-type": { borderRight: "none" },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 34, lineHeight: "34px", color: stat.color, fontWeight: 700 }}>{stat.value}</Typography>
                    <Typography
                      sx={{
                        width: stat.label === "TOTAL" ? 50 : stat.label === "COMPLETED" ? 85 : 67,
                        height: 15,
                        mx: "auto",
                        mt: 0.75,
                        fontFamily: INTAKE_FONT,
                        fontWeight: 700,
                        fontStyle: "normal",
                        fontSize: 14,
                        lineHeight: "15px",
                        letterSpacing: "0.5px",
                        textAlign: "center",
                        textTransform: "uppercase",
                        color: "#626262",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ px: 1.25, py: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, borderBottom: `1px solid ${INTAKE_COLORS.border}`, flexWrap: { xs: "wrap", lg: "nowrap" }, ml: "-1px", width: "calc(100% + 2px)", boxSizing: "border-box" }} data-export-hide>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: { xs: "wrap", lg: "nowrap" }, overflowX: { xs: "visible", lg: "auto" }, minWidth: 0, pr: 0.5 }}>
                  {[
                    { key: "all", label: `All Sections (${sections.length})`, width: 109 },
                    { key: "required", label: `Required only (${requiredSections})`, width: 121 },
                    { key: "optional", label: `Optional (${fmt2(optionalSections)})`, width: 92 },
                  ].map((pill) => {
                    const active = sectionFilter === pill.key;
                    return (
                      <Button
                        key={pill.key}
                        onClick={() => setSectionFilter(pill.key)}
                        sx={{
                          width: "auto",
                          height: 32,
                          minHeight: 32,
                          minWidth: pill.width,
                          borderRadius: "12px",
                          px: "8px",
                          py: "4px",
                          border: "1px solid #E6E6E6",
                          bgcolor: active ? "#005BF0" : "#FFFFFF",
                          color: active ? "#FFFFFF" : "#555555",
                          fontFamily: INTAKE_FONT,
                          fontWeight: active ? 400 : 700,
                          fontSize: 13,
                          lineHeight: "24px",
                          letterSpacing: 0,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: active ? "#005BF0" : "#F5F5F5",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {pill.label}
                      </Button>
                    );
                  })}
                </Box>
                <Button
                  onClick={() => {
                    const next = {};
                    shownSections.forEach((s) => { next[s.id] = !allShownExpanded; });
                    setExpandedSections((prev) => ({ ...prev, ...next }));
                  }}
                  sx={{
                    ...ghostPrimaryBtnSx,
                    width: 94,
                    minWidth: 94,
                    flexShrink: 0,
                    ml: { xs: "auto", lg: 0 },
                  }}
                >
                  {allShownExpanded ? "Collapse All" : "Expand All"}
                </Button>
              </Box>

              <Stack divider={null} spacing={0}>
                {shownSections.map((s) => {
                  const answered = (capturedBySection[s.id] || []).length;
                  const total = s.items?.length || 0;
                  const completionPct = computeSectionCompletionPct(captured, s, s.id);
                  const expanded = Boolean(expandedSections[s.id]);
                  const status = sectionStatus(answered, total);
                  const statusStyle = SECTION_STATUS_STYLE[status];
                  const progressPct = completionPct;
                  const scoreColor = completionPct >= 100 ? "#14B871" : completionPct >= 40 ? "#E4A11A" : "#707070";
                  const sectionItems = s.items || [];
                  return (
                    <Box key={s.id} sx={{ borderBottom: `1px solid ${INTAKE_COLORS.border}`, ml: "-1px", width: "calc(100% + 2px)", boxSizing: "border-box", "&:last-of-type": { borderBottom: "none" } }}>
                      <Box
                        onClick={() => setExpandedSections((prev) => ({ ...prev, [s.id]: !expanded }))}
                        sx={{
                          px: 1.25,
                          py: 1.25,
                          cursor: "pointer",
                          bgcolor: expanded ? INTAKE_COLORS.g1 : "transparent",
                          "&:hover": { bgcolor: INTAKE_COLORS.g1 },
                        }}
                      >
                      <Box
                        sx={sectionRowGrid({ alignItems: "flex-start", mb: 0.75 })}
                      >
                        <Box sx={chevronCellSx}>
                          <Box
                            component="button"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSections((prev) => ({ ...prev, [s.id]: !expanded }));
                            }}
                            sx={{ border: 0, bgcolor: "transparent", p: 0, m: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", flexShrink: 0 }}
                          >
                            {expanded ? (
                              <ExpandMoreIcon sx={{ fontSize: 18, color: "#5D6268" }} />
                            ) : (
                              <ChevronRightIcon sx={{ fontSize: 18, color: "#5D6268" }} />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          {isSectionRequiredForIntake(s) && (
                            <Typography
                              sx={{
                                width: { xs: "auto", md: 44 },
                                minHeight: 14,
                                fontFamily: INTAKE_FONT,
                                fontWeight: 700,
                                fontSize: 8,
                                lineHeight: "13.5px",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                color: "#707070",
                                mb: 0.25,
                              }}
                            >
                              Required
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              width: "100%",
                              minHeight: 20,
                              fontFamily: INTAKE_FONT,
                              fontWeight: 700,
                              fontSize: 16,
                              lineHeight: "19.5px",
                              letterSpacing: "-0.32px",
                              color: "#1F2335",
                            }}
                          >
                            {s.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: "4px",
                              width: STATUS_BADGE_WIDTH,
                              boxSizing: "border-box",
                              height: 24,
                              borderRadius: "16px",
                              pl: "8px",
                              pr: "12px",
                              bgcolor: statusStyle.badgeBg,
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {sectionStatusIcon(status)}
                            </Box>
                            <Typography
                              sx={{
                                fontFamily: INTAKE_FONT,
                                fontWeight: 700,
                                fontSize: 11,
                                lineHeight: 1,
                                color: statusStyle.countColor,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {answered}/{total}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: INTAKE_FONT,
                              fontWeight: 700,
                              fontSize: 12,
                              lineHeight: 1,
                              color: scoreColor,
                              minWidth: 36,
                              textAlign: "right",
                            }}
                          >
                            {Math.round(completionPct)}%
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={sectionRowGrid()}>
                        <Box />
                        <Box sx={{ gridColumn: "2 / -1", minWidth: 0 }}>
                          <Box
                            sx={{
                              width: "100%",
                              height: 8,
                              borderRadius: "4px",
                              bgcolor: "#F5F5F5",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                height: "100%",
                                width: `${progressPct}%`,
                                borderRadius: "4px",
                                background: `linear-gradient(90deg, ${statusStyle.barColor} 0%, ${statusStyle.barColor} 100%)`,
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={sectionRowGrid({ mt: 0.5 })}>
                        <Box />
                        <Typography
                          sx={{
                            fontFamily: INTAKE_FONT,
                            fontSize: 12,
                            fontWeight: 400,
                            color: "#707070",
                          }}
                        >
                          {total} facts
                        </Typography>
                        <Box />
                      </Box>
                      </Box>

                      {expanded && (
                        <Box sx={{ pb: 1.25, bgcolor: INTAKE_COLORS.surface }}>
                          <Stack spacing={0}>
                            {sectionItems.length === 0 ? (
                              <Typography sx={{ fontSize: 13, color: "#777777", fontStyle: "italic", py: 0.75, px: 1.25 }}>
                                No facts in this section.
                              </Typography>
                            ) : (
                              sectionItems.map((item) => (
                                <SummaryFactRow
                                  key={item.id}
                                  item={item}
                                  capd={captured[item.id]}
                                />
                              ))
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>

          {outputs?.trust_profile && (
            <Box sx={{ px: INTAKE_PROFILE_ROW_PX, py: 2, borderTop: `1px solid ${INTAKE_COLORS.border}` }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2, color: INTAKE_COLORS.black }}>
                Trust profile
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2 }}>
                {Object.entries(outputs.trust_profile).map(([k, v]) => (
                  <Box key={k} sx={{ border: `1px solid ${INTAKE_COLORS.g2}`, borderRadius: 2, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: INTAKE_COLORS.g5, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>
                      {trustLabels[k] || k}
                    </Typography>
                    {typeof v?.score === "number" && (
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: INTAKE_COLORS.blue, lineHeight: 1.1 }}>
                        {v.score}%
                      </Typography>
                    )}
                    {v?.rationale && (
                      <Typography sx={{ fontSize: 12.5, color: INTAKE_COLORS.g5, mt: 0.5 }}>
                        {v.rationale}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {Array.isArray(outputs?.risks) && outputs.risks.length > 0 && (
            <Box sx={{ px: INTAKE_PROFILE_ROW_PX, py: 2, borderTop: `1px solid ${INTAKE_COLORS.border}` }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2, color: INTAKE_COLORS.black }}>
                Risk register
              </Typography>
              <Stack spacing={1.25}>
                {outputs.risks.map((r, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      border: `1px solid ${INTAKE_COLORS.g2}`,
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: severityBg(r.severity),
                    }}
                  >
                    <ReportProblemOutlinedIcon sx={{ color: severityColor(r.severity), mt: "2px" }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          label={String(r.severity || "info").toUpperCase()}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10.5,
                            fontWeight: 700,
                            bgcolor: severityColor(r.severity),
                            color: "#fff",
                          }}
                        />
                        {r.category && (
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: INTAKE_COLORS.g5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            {r.category}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 13.5, color: INTAKE_COLORS.black, mb: r.mitigation ? 0.5 : 0 }}>
                        {r.description || r.title || ""}
                      </Typography>
                      {r.mitigation && (
                        <Typography sx={{ fontSize: 12.5, color: INTAKE_COLORS.g5 }}>
                          <strong>Mitigation:</strong> {r.mitigation}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          </Box>
        </Card>
      </Box>

      <Box
        data-export-hide
        sx={{
          flexShrink: 0,
          height: 56,
          boxSizing: "border-box",
          px: INTAKE_PROFILE_ROW_PX,
          py: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: INTAKE_COLORS.surface,
          borderTop: `1px solid ${INTAKE_COLORS.border}`,
          boxShadow: "0px -1px 8px 0px #2122260A",
          zIndex: 2,
        }}
      >
        <Button
          onClick={() => navigate(canWriteChat ? `/chat/${projectId}` : "/home")}
          sx={ghostPrimaryBtnSx}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button onClick={handleExport} disabled={exporting} sx={ghostPrimaryBtnSx}>
            {exporting ? "Downloading…" : "Download Summary"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disableRipple
            disableFocusRipple
            disabled={!postGate0FeaturesEnabled()}
            onClick={() => navigate(`/readiness/${projectId}`)}
            sx={footerPrimaryBtnSx}
          >
            Gate 1 readiness
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast("")}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
